// In-App merge tags, read exactly as both SDKs resolve them (shared vectors:
// fixtures/inapp-merge-tag-vectors.json, owner push-ios). Syntaxes: {{key}}, {{key|fallback}},
// $key, ${key}, ${key|fallback}. The portal uses this to insert variables (R-1), to flag a label
// that would render empty (X-5) and to flag unknown variable names (F11).

const TOKEN = /\{\{([^{}]*)\}\}|\$\{([^{}]*)\}|\$([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z0-9_]+)*)/g;
const KEY = /^[A-Za-z0-9_.]+$/;

const ENTITIES: Record<string, string> = { lt: "<", gt: ">", amp: "&", quot: '"', apos: "'", nbsp: " " };
// Exported for P3 (DA-BUG-1391): the editor decodes stored HTML entities once on load, as the
// device + preview do at render, so author markup isn't shown escaped (`&lt;i&gt;`).
export function decodeEntitiesOnce(s: string): string {
  return s.replace(/&(#x[0-9a-fA-F]+|#\d+|[A-Za-z]+);/g, (m, e: string) => {
    if (e[0] === "#") {
      const n = e[1] === "x" || e[1] === "X" ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(n) && n >= 0 && n <= 0x10ffff ? String.fromCodePoint(n) : m;
    }
    return Object.prototype.hasOwnProperty.call(ENTITIES, e) ? ENTITIES[e] : m;
  });
}

interface Token { raw: string; key: string; fallback: string | null; valid: boolean }
function readToken(m: RegExpExecArray): Token {
  const inner = m[1] != null ? m[1] : m[2];
  if (inner == null) return { raw: m[0], key: m[3], fallback: null, valid: true }; // bare $key
  const bar = inner.indexOf("|");
  const key = (bar === -1 ? inner : inner.slice(0, bar)).trim();
  const fallback = bar === -1 ? null : inner.slice(bar + 1).trim();
  return { raw: m[0], key, fallback, valid: key === "" || KEY.test(key) };
}

const show = (v: unknown): string | null => {
  if (v == null) return null;
  const s = typeof v === "string" ? v : typeof v === "boolean" || typeof v === "number" ? String(v) : null;
  return s == null ? null : s.slice(0, 500);
};

// `keepRaw(key)`: a key with no value and no fallback stays as its tag (Preview 1: the identifying keys
// email / phone are never filled server-side, so Preview shows the tag instead of an empty string).
export function resolveMergeTags(text: string, props: Record<string, unknown> = {}, keepRaw?: (key: string) => boolean): string {
  const src = decodeEntitiesOnce(String(text ?? ""));
  return src.replace(TOKEN, (...args: any[]) => {
    const t = readToken(args as unknown as RegExpExecArray);
    if (!t.valid) return t.raw;
    const v = t.key ? show(props[t.key]) : null;
    if ((v == null || v === "") && !t.fallback && keepRaw && keepRaw(t.key)) return t.raw;
    return v != null && v !== "" ? v : t.fallback ?? "";
  });
}

// Every variable a string uses ({ key, fallback }); "" = empty braces. Malformed tokens are literal.
export function mergeTagKeys(text: string): { key: string; fallback: boolean }[] {
  const out: { key: string; fallback: boolean }[] = [];
  const re = new RegExp(TOKEN.source, "g");
  let m: RegExpExecArray | null;
  const src = String(text ?? "");
  while ((m = re.exec(src))) {
    const t = readToken(m);
    if (t.valid) out.push({ key: t.key, fallback: t.fallback != null && t.fallback !== "" });
  }
  return out;
}

// What a customer with NONE of the variables set would see (every token → its fallback or "").
export const worstCaseText = (text: string): string => resolveMergeTags(text, {});
